import { Component, EventEmitter, Input, Output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgRecolorDirective } from 'src/app/directives/svg-recolor.directive';
import { LanguageService } from 'src/app/services/account/language.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { WidgetService } from 'src/app/services/platform/widget.service';
import { ECarType } from 'src/app/models/ecar-type';
import { ValueIntegerPipe } from 'src/app/pipes/value-integer.pipe';
import { EvSliderComponent } from '../../../ui-components/ev-slider/ev-slider.component';
import { ModalOptionPanelComponent } from '../../modal-option-panel/modal-option-panel.component';

export type ChargingPage =
  | { page: 'stations', mode: 'favorite' | 'disabled' }
  | { page: 'operators', mode: 'prefer' | 'avoid' };

@Component({
  standalone: true,
  selector: 'app-charging-options',
  templateUrl: './charging-options.component.html',
  styleUrls: ['./charging-options.component.scss'],
  imports: [CommonModule, EvSliderComponent, ValueIntegerPipe, ModalOptionPanelComponent, SvgRecolorDirective]
})
export class ChargingOptionsComponent {
  @Input() selectedCar: ECarType | null = null;
  @Input() fastChargeLimit: number = 90;
  @Input() fastChargeLimitMin: number = 75;
  @Input() fastChargeLimitMax: number = 90;
  @Input() batteryStateOfHealth: number = 100;
  @Input() batteryStateOfHealthMin: number = 50;
  @Input() batteryStateOfHealthMax: number = 110;
  @Input() selectedChargerTypes: boolean[] = [false, false, false, false, false, false];
  @Input() chargerTypes: { name: string, icon: string }[] = [];

  @Output() fastChargeLimitChange = new EventEmitter<number>();
  @Output() batteryStateOfHealthChange = new EventEmitter<number>();
  /** Emits the full new selection — the input array is never mutated in place. */
  @Output() chargerTypesChange = new EventEmitter<boolean[]>();
  /** Asks the parent dialog to open one of the charging station/operator pages. */
  @Output() openChargingPage = new EventEmitter<ChargingPage>();

  // The same solid recolor the map charging popup applies to its plug icons.
  // Resolved here rather than handed to the directive as a `--var` name: the
  // variable differs per skin, and only a changed input value makes the
  // directive re-render, so track the map signal and re-resolve on a skin flip.
  public readonly plugIconColor = computed(() => {
    this.settingsService.getMap();
    return getComputedStyle(document.body).getPropertyValue('--gradient-color-over-gradient').trim();
  });

  // Widget `stationlists=0` / `operatorlists=0` hide the matching button group; both are
  // always on outside widget mode.
  private readonly widgetService = inject(WidgetService);
  public readonly stationListsEnabled = this.widgetService.stationListsEnabled();
  public readonly operatorListsEnabled = this.widgetService.operatorListsEnabled();

  constructor(
    public languageService: LanguageService,
    public settingsService: SettingsService
  ) {}

  public get favoriteStationCount(): number {
    return this.settingsService.getFavoriteChargingStationIds()?.length || 0;
  }
  public get disabledStationCount(): number {
    return this.settingsService.getDisabledChargingStationIds()?.length || 0;
  }
  public get favoriteOperatorCount(): number {
    return this.settingsService.getFavoriteChargingOperatorIds()?.length || 0;
  }
  public get disabledOperatorCount(): number {
    return this.settingsService.getDisabledChargingOperatorIds()?.length || 0;
  }

  public toggleChargerType(i: number): void {
    this.chargerTypesChange.emit(this.selectedChargerTypes.map((selected, index) => index === i ? !selected : selected));
  }
}
